import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApiToast } from '../../hooks/useApiToast';
import InvestmentPoolCard from './InvestmentPoolCard';
import InvestmentModal from './InvestmentModal';
import WithdrawalModal from './WithdrawalModal';
import { useInvestmentPoolWebSocket } from '../../hooks/useInvestmentPoolWebSocket';
import './InvestmentTab.css';

const InvestmentTab = () => {
  const { tokens } = useAuth();
  const { handleTokenOperation } = useApiToast();

  const [investmentPools, setInvestmentPools] = useState([]);
  const [userInvestments, setUserInvestments] = useState([]);
  const [investmentHistory, setInvestmentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [investmentModal, setInvestmentModal] = useState({ isOpen: false, pool: null });
  const [withdrawalModal, setWithdrawalModal] = useState({ isOpen: false, investment: null });

  // Investment summary
  const [investmentSummary, setInvestmentSummary] = useState({
    totalInvested: 0,
    currentValue: 0,
    totalReturn: 0,
    totalReturnPercentage: 0,
    activeInvestments: 0
  });

  // Get pool IDs for WebSocket subscription
  const poolIds = investmentPools.map(pool => pool.id);

  // WebSocket integration for real-time pool updates
  const {
    poolData: wsPoolData,
    isConnected: wsConnected,
    subscribedPools,
    subscribeToPoolUpdates,
    unsubscribeFromPoolUpdates,
    getPoolData,
    getPoolPerformance,
    getPoolSharePrice,
    refreshAllPools
  } = useInvestmentPoolWebSocket(poolIds, {
    onPoolUpdate: (poolId, data) => {
      console.log(`🏦 Real-time pool ${poolId} update:`, data);

      // Update the pool in our local state
      setInvestmentPools(prev => prev.map(pool =>
        pool.id === poolId ? { ...pool, ...data.pool } : pool
      ));
    },
    onError: (error) => {
      console.error('Investment pool WebSocket error:', error);
    },
    autoSubscribe: true
  });

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepump.urion.live/api';

  useEffect(() => {
    if (tokens?.access_token) {
      fetchInvestmentData();
    }
  }, [tokens]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const fetchInvestmentData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch investment pools
      const poolsData = await makeAuthenticatedRequest('/investment/pools');
      console.log('Investment pools data:', poolsData);
      if (poolsData.success) {
        setInvestmentPools(poolsData.data.pools || []);
        console.log('Set investment pools:', poolsData.data.pools);
      }

      // Fetch user investments
      const investmentsData = await makeAuthenticatedRequest('/investment/user-investments');
      if (investmentsData.success) {
        setUserInvestments(investmentsData.data.investments || []);
        setInvestmentSummary(investmentsData.data.summary || {
          totalInvested: 0,
          currentValue: 0,
          totalReturn: 0,
          totalReturnPercentage: 0,
          activeInvestments: 0
        });
      }

      // Fetch investment history
      const historyData = await makeAuthenticatedRequest('/investment/history');
      if (historyData.success) {
        setInvestmentHistory(historyData.data.history || []);
      }

    } catch (error) {
      setError(error.message);
      console.error('Error fetching investment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openInvestmentModal = (pool) => {
    setInvestmentModal({ isOpen: true, pool });
  };

  const closeInvestmentModal = () => {
    setInvestmentModal({ isOpen: false, pool: null });
    // Refresh investment data after investment
    fetchInvestmentData();
  };

  const openWithdrawalModal = (investment) => {
    setWithdrawalModal({ isOpen: true, investment });
  };

  const closeWithdrawalModal = () => {
    setWithdrawalModal({ isOpen: false, investment: null });
    // Refresh investment data after withdrawal
    fetchInvestmentData();
  };

  const formatNumber = (num) => {
    // Convert to number and handle edge cases
    const numValue = parseFloat(num);
    if (isNaN(numValue) || numValue === 0) return '0.000';
    if (numValue >= 1e6) return `${(numValue / 1e6).toFixed(2)}M`;
    if (numValue >= 1e3) return `${(numValue / 1e3).toFixed(2)}K`;
    return numValue.toFixed(3);
  };

  const formatPercentage = (percent) => {
    const percentValue = parseFloat(percent);
    if (isNaN(percentValue) || percentValue === 0) return '0.00%';
    const formatted = percentValue.toFixed(2);
    return `${percentValue >= 0 ? '+' : ''}${formatted}%`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown Date';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid Date';
    }
  };

  if (loading) {
    return (
      <div className="investment-tab">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading investment pools...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="investment-tab">
        <div className="error-state">
          <div className="error-icon">❌</div>
          <h3>Error Loading Investment Data</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={fetchInvestmentData}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="investment-tab">
      <div className="investment-header">
        <h3>Investment Pools</h3>
        <p>Invest your SOL with professional traders and earn returns</p>
      </div>

      {/* User Investment Summary */}
      {userInvestments.length > 0 && (
        <div className="investment-summary">
          <h4>My Investment Summary</h4>
          <div className="summary-grid">
            <div className="summary-metric">
              <span className="metric-label">Total Invested</span>
              <span className="metric-value">{formatNumber(investmentSummary.totalInvested)} SOL</span>
            </div>
            <div className="summary-metric">
              <span className="metric-label">Current Value</span>
              <span className="metric-value">{formatNumber(investmentSummary.currentValue)} SOL</span>
            </div>
            <div className="summary-metric">
              <span className="metric-label">Total Return</span>
              <span className={`metric-value ${investmentSummary.totalReturn >= 0 ? 'positive' : 'negative'}`}>
                {formatNumber(Math.abs(investmentSummary.totalReturn))} SOL
              </span>
            </div>
            <div className="summary-metric">
              <span className="metric-label">Return %</span>
              <span className={`metric-value ${investmentSummary.totalReturnPercentage >= 0 ? 'positive' : 'negative'}`}>
                {formatPercentage(investmentSummary.totalReturnPercentage)}
              </span>
            </div>
            <div className="summary-metric">
              <span className="metric-label">Active Investments</span>
              <span className="metric-value">{investmentSummary.activeInvestments}</span>
            </div>
          </div>
        </div>
      )}

      {/* Investment Pools Grid */}
      <div className="investment-pools-section">
        <h4>Available Investment Pools</h4>
        {investmentPools.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🏦</div>
            <h3>No Investment Pools Available</h3>
            <p>Check back later for new investment opportunities</p>
          </div>
        ) : (
          <div className="investment-pools-grid">
            {investmentPools.map(pool => {
              const userInvestment = userInvestments.find(inv => inv.pool_id === pool.id);
              return (
                <InvestmentPoolCard
                  key={pool.id}
                  pool={pool}
                  userInvestment={userInvestment}
                  onInvest={() => openInvestmentModal(pool)}
                  onWithdraw={() => userInvestment && openWithdrawalModal(userInvestment)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* My Active Investments */}
      {userInvestments.length > 0 && (
        <div className="my-investments-section">
          <h4>My Active Investments</h4>
          <div className="investments-list">
            {userInvestments.map(investment => (
              <div key={investment.id} className="investment-item">
                <div className="investment-info">
                  <div className="pool-name">{investment.pool_name}</div>
                  <div className="investment-details">
                    <span className="detail">
                      <span className="label">Invested:</span>
                      <span className="value">{formatNumber(investment.amount_invested)} SOL</span>
                    </span>
                    <span className="detail">
                      <span className="label">Current Value:</span>
                      <span className="value">{formatNumber(investment.current_value)} SOL</span>
                    </span>
                    <span className="detail">
                      <span className="label">Return:</span>
                      <span className={`value ${investment.unrealized_gains >= 0 ? 'positive' : 'negative'}`}>
                        {formatPercentage((investment.current_value - investment.amount_invested) / investment.amount_invested * 100)}
                      </span>
                    </span>
                    <span className="detail">
                      <span className="label">Date:</span>
                      <span className="value">{formatDate(investment.investment_date)}</span>
                    </span>
                  </div>
                </div>
                <div className="investment-actions">
                  <button
                    className="add-more-btn"
                    onClick={() => {
                      const pool = investmentPools.find(p => p.id === investment.pool_id);
                      if (pool) openInvestmentModal(pool);
                    }}
                  >
                    Add More
                  </button>
                  <button
                    className="withdraw-btn"
                    onClick={() => openWithdrawalModal(investment)}
                    disabled={investment.status !== 'active'}
                  >
                    Withdraw
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Investment History */}
      {investmentHistory.length > 0 && (
        <div className="investment-history-section">
          <h4>Investment History</h4>
          <div className="history-list">
            {investmentHistory.slice(0, 10).map(historyItem => {
              // Safety check for required fields
              if (!historyItem || !historyItem.id) {
                return null;
              }

              return (
                <div key={historyItem.id} className="history-item">
                  <div className="history-info">
                    <div className="history-type">
                      <span className={`type-badge ${historyItem.type || 'unknown'}`}>
                        {historyItem.type === 'deposit' ? '💰' :
                          historyItem.type === 'withdrawal' ? '💸' : '💳'}
                        {historyItem.type ?
                          historyItem.type.charAt(0).toUpperCase() + historyItem.type.slice(1) :
                          'Unknown'
                        }
                      </span>
                    </div>
                    <div className="history-details">
                      <div className="pool-name">{historyItem.pool_name || 'Unknown Pool'}</div>
                      <div className="amount">
                        {historyItem.type === 'withdrawal' ? '-' : '+'}{formatNumber(historyItem.amount || 0)} SOL
                      </div>
                      <div className="date">{historyItem.created_at ? formatDate(historyItem.created_at) : 'Unknown Date'}</div>
                    </div>
                  </div>
                  {(historyItem.fees_paid || 0) > 0 && (
                    <div className="fees-info">
                      <span className="fees-label">Fees:</span>
                      <span className="fees-value">{formatNumber(historyItem.fees_paid)} SOL</span>
                    </div>
                  )}
                </div>
              );
            }).filter(Boolean)}
          </div>
          {investmentHistory.length > 10 && (
            <button className="view-all-history-btn">
              View All History ({investmentHistory.length} items)
            </button>
          )}
        </div>
      )}

      {/* Investment Modal */}
      {investmentModal.isOpen && (
        <InvestmentModal
          isOpen={investmentModal.isOpen}
          onClose={closeInvestmentModal}
          pool={investmentModal.pool}
        />
      )}

      {/* Withdrawal Modal */}
      {withdrawalModal.isOpen && (
        <WithdrawalModal
          isOpen={withdrawalModal.isOpen}
          onClose={closeWithdrawalModal}
          investment={withdrawalModal.investment}
        />
      )}
    </div>
  );
};

export default InvestmentTab;